import Heading from '@atlaskit/heading';
import { Stack, Box, Flex, xcss } from '@atlaskit/primitives';
import { useDatabaseContent } from '@databases/database-state';
import {
  type BasicDatabase,
  type BrokenDatabase,
  type EntryCreateOptions,
  type EntryWithContext,
} from '@databases/types/database';
import React, { type FunctionComponent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import EntryEmptyState from '../../assets/entry_empty_state.svg';
import searchError from '../../assets/search_error.svg';
import searchIllustration from '../../assets/search_illustration.svg';
import { messages } from '../../i18n';
import {
  PreviewPlaceholderHeaderText,
  PreviewPlaceholderText,
} from '../embedReference/embedReference.styles';
import { isCreate } from '../entryPicker/DatabaseEntryPicker';

const iconWrapperStyles = xcss({
  padding: 'space.300',
});

export type DatabasePreviewComponentProps = {
  contentId: string;
  canEditContent: boolean;
  couldEditFields: boolean;
  canEditFields: boolean;
  isContentEditable?: boolean;
  outerContainerRef: HTMLElement | null;
  setOuterContainerRef: (ref: HTMLElement | null) => void;
  hasSelection: boolean;
};

export const DatabasePlaceholder: FunctionComponent<{ error?: boolean }> = ({
  error = false,
}) => {
  const { formatMessage } = useIntl();
  const isNewEmbedDBModalEnabled = isFeatureFlagEnabled(
    'databases-frontend-embed-database-modal'
  );

  return (
    <Stack alignInline="center" space="space.400">
      <Box xcss={iconWrapperStyles}>
        <img
          width={108}
          height={108}
          role="presentation"
          src={error ? searchError : searchIllustration}
        />
      </Box>
      {error && (
        <PreviewPlaceholderHeaderText>
          {formatMessage(messages['reference-preview.broken-header'])}
        </PreviewPlaceholderHeaderText>
      )}
      {isNewEmbedDBModalEnabled ? (
        <Heading size="small">
          {formatMessage(
            messages[
              error
                ? 'reference-preview.broken-description'
                : 'reference-preview.placeholder'
            ]
          )}
        </Heading>
      ) : (
        <PreviewPlaceholderText>
          {formatMessage(
            messages[
              error
                ? 'reference-preview.broken-description'
                : 'reference-preview.placeholder'
            ]
          )}
        </PreviewPlaceholderText>
      )}
    </Stack>
  );
};

export const EntryPlaceHolder: FunctionComponent = () => {
  const { formatMessage } = useIntl();

  return (
    <Stack alignInline="center">
      <Box xcss={iconWrapperStyles}>
        <img
          width={242}
          height={111}
          role="presentation"
          src={EntryEmptyState}
          alt={formatMessage(messages['reference-preview.entry-placeholder-header'])}
        />
      </Box>
      <PreviewPlaceholderHeaderText>
        {formatMessage(messages['reference-preview.entry-placeholder-header'])}
      </PreviewPlaceholderHeaderText>
      <PreviewPlaceholderText>
        {formatMessage(messages['reference-preview.entry-placeholder-description'])}
      </PreviewPlaceholderText>
    </Stack>
  );
};

const DatabaseComponentContainer = ({
  contentId,
  DatabasePreviewComponent,
}: {
  contentId: string;
  DatabasePreviewComponent: FunctionComponent<DatabasePreviewComponentProps>;
}) => {
  const { setContentId } = useDatabaseContent();

  useEffect(() => {
    setContentId(contentId);
  }, [contentId, setContentId]);

  const [outerContainerRef, setOuterContainerRef] = useState<HTMLElement | null>(null);

  return (
    <DatabasePreviewComponent
      contentId={contentId}
      isContentEditable={false}
      canEditContent={false}
      couldEditFields={false}
      canEditFields={false}
      outerContainerRef={outerContainerRef}
      setOuterContainerRef={setOuterContainerRef}
      hasSelection={false}
    />
  );
};

const placeholderContainerStyles = xcss({
  width: '100%',
});

const embedReferenceContainerStyles = xcss({
  maxWidth: '100%',
  overflow: 'hidden',
});

const previewContainerStyles = xcss({
  marginInline: 'space.100',
  minHeight: '0',
  height: '100%',
  paddingBottom: 'space.100',
  overflow: 'auto',
});

const valueContainerStyles = xcss({
  marginLeft: 'space.200',
  marginTop: 'space.400',
});

const updatedValueStyles = xcss({
  height: '384px',
  marginTop: 'space.0',
  overflow: 'auto',
});

const legacyHeightStyles = xcss({
  height: '600px',
});

const heightStyles = xcss({
  height: '384px',
});
export interface DatabasePreviewProps {
  databaseError: boolean;
  selectedDatabase: BasicDatabase | BrokenDatabase | undefined;
  selectedEntry?: EntryWithContext | EntryCreateOptions | undefined;
  selectedField?: string | undefined;
  DatabasePreviewComponent: FunctionComponent<DatabasePreviewComponentProps>;
  EntryPreviewComponent: FunctionComponent<{
    contentId: string;
    entryId: string;
    isContentEditable?: boolean;
  }>;
  ValuePreviewComponent: FunctionComponent<{
    contentId: string;
    entryId: string;
    fieldId: string;
  }>;
  ToolbarComponent: React.FunctionComponent<{
    contentId: string;
    actions: any;
    canEditFields: boolean;
    canEditContent: boolean;
    showViewSettings?: boolean;
    showName?: boolean;
  }>;
}

export const DatabasePreview: FunctionComponent<DatabasePreviewProps> = ({
  databaseError,
  selectedDatabase,
  DatabasePreviewComponent,
  EntryPreviewComponent,
  ValuePreviewComponent,
  ToolbarComponent,
  selectedEntry,
  selectedField,
}) => {
  const isNewEmbedDBModalEnabled = isFeatureFlagEnabled(
    'databases-frontend-embed-database-modal'
  );

  if (databaseError) {
    return (
      <Stack
        alignInline="center"
        alignBlock="center"
        xcss={[placeholderContainerStyles, isNewEmbedDBModalEnabled && heightStyles]}
      >
        <DatabasePlaceholder error={true} />
      </Stack>
    );
  }
  if (selectedField && selectedEntry && 'entry' in selectedEntry && selectedDatabase) {
    return (
      <Box xcss={[valueContainerStyles, isNewEmbedDBModalEnabled && updatedValueStyles]}>
        <ValuePreviewComponent
          contentId={selectedDatabase.contentId}
          entryId={selectedEntry.entry.id}
          fieldId={selectedField}
        />
      </Box>
    );
  }
  if (selectedEntry && isCreate(selectedEntry)) {
    return (
      <Flex
        xcss={[
          embedReferenceContainerStyles,
          !isNewEmbedDBModalEnabled && legacyHeightStyles,
        ]}
      >
        <Stack
          alignInline="center"
          alignBlock="center"
          xcss={[placeholderContainerStyles, isNewEmbedDBModalEnabled && heightStyles]}
        >
          <EntryPlaceHolder />
        </Stack>
      </Flex>
    );
  }

  if (selectedEntry && 'entry' in selectedEntry && selectedDatabase) {
    return (
      <Stack xcss={[previewContainerStyles, isNewEmbedDBModalEnabled && heightStyles]}>
        <EntryPreviewComponent
          contentId={selectedDatabase.contentId}
          entryId={selectedEntry.entry.id}
          isContentEditable={false}
        />
      </Stack>
    );
  }
  if (selectedDatabase) {
    return isNewEmbedDBModalEnabled ? (
      <Stack
        xcss={xcss({
          height: '384px',
          overflow: 'hidden',
        })}
      >
        <ToolbarComponent
          contentId={selectedDatabase.contentId}
          canEditContent={false}
          canEditFields={false}
          actions={[]}
          showViewSettings={false}
          showName={false}
        />
        <DatabaseComponentContainer
          contentId={selectedDatabase.contentId}
          DatabasePreviewComponent={DatabasePreviewComponent}
        />
      </Stack>
    ) : (
      <DatabaseComponentContainer
        contentId={selectedDatabase.contentId}
        DatabasePreviewComponent={DatabasePreviewComponent}
      />
    );
  }

  return (
    <Flex
      xcss={[
        embedReferenceContainerStyles,
        !isNewEmbedDBModalEnabled && legacyHeightStyles,
      ]}
    >
      <Stack
        alignInline="center"
        alignBlock="center"
        xcss={[placeholderContainerStyles, isNewEmbedDBModalEnabled && heightStyles]}
      >
        <DatabasePlaceholder />
      </Stack>
    </Flex>
  );
};
